/**
 * AI Prompts admin API client (Phase X.3.7c C3).
 *
 * Read-only prompt viewer + the change-request queue under /v1/admin-review/.
 * Owner/admin-gated server-side (manage_team). Mirrors src/lib/aiCaps.ts.
 */

import { writeFetch } from './writeFetch';
import { apiErrorFrom } from './api';

export interface PromptViewEntry {
  prompt_key: string;
  tool_key: string;
  label: string;
  version: number;
  template: string;
}

export interface PromptsResponse {
  prompts: PromptViewEntry[];
  request_id?: string;
}

export type PromptChangeStatus = 'open' | 'applied' | 'declined';

export interface PromptChangeRequest {
  id: string;
  tool_key: string;
  description: string;
  suggested_wording: string | null;
  status: PromptChangeStatus;
  requested_by_user_id: string;
  requested_by_display: string | null;
  resolved_by_user_id: string | null;
  resolved_by_display: string | null;
  resolved_at: string | null; // ISO on the wire
  created_at: string;
}

export interface PromptChangeRequestsResponse {
  rows: PromptChangeRequest[];
  request_id?: string;
}

export interface CreatePromptChangeInput {
  tool_key: string;
  description: string;
  suggested_wording: string | null;
}

async function requestJson(input: string, init: RequestInit = {}): Promise<unknown> {
  const r = await writeFetch(input, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!r.ok) throw await apiErrorFrom(r, `${init.method ?? 'GET'} ${input}`);
  return r.json();
}

const adminBase = (slug: string): string => `/api/t/${encodeURIComponent(slug)}/v1/admin-review`;

export const aiPromptsApi = {
  async prompts(tenantSlug: string, signal?: AbortSignal): Promise<PromptsResponse> {
    return (await requestJson(`${adminBase(tenantSlug)}/prompts`, { signal })) as PromptsResponse;
  },

  async listRequests(tenantSlug: string, signal?: AbortSignal): Promise<PromptChangeRequestsResponse> {
    return (await requestJson(`${adminBase(tenantSlug)}/prompt-change-requests`, {
      signal,
    })) as PromptChangeRequestsResponse;
  },

  async createRequest(tenantSlug: string, input: CreatePromptChangeInput): Promise<{ id: string }> {
    return (await requestJson(`${adminBase(tenantSlug)}/prompt-change-requests`, {
      method: 'POST',
      body: JSON.stringify(input),
    })) as { id: string };
  },

  async resolveRequest(
    tenantSlug: string,
    id: string,
    status: 'applied' | 'declined',
  ): Promise<{ saved: true }> {
    return (await requestJson(
      `${adminBase(tenantSlug)}/prompt-change-requests/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
    )) as { saved: true };
  },
};
